/**
 * InfrastructureView.tsx
 *
 * UX model: one tab = one API call = fast response every time.
 *
 * - User lands → EC2 tab loads automatically (first tab)
 * - Clicking any other tab fires GET /infrastructure/resources?resource_type=X
 * - Results for each tab are cached in memory so switching back is instant
 * - No "load everything" call ever happens
 *
 * API: GET /infrastructure/resources?resource_type={type}&provider={provider}
 * Create APIs: POST /infrastructure/create/aws/{type}
 *              POST /infrastructure/create/azure/{type}
 */

import React, { useState, useCallback, useRef } from 'react';
import axiosInstance from '../Core/RestHelper';

// ─── Types ────────────────────────────────────────────────────────────────────

type Status = 'ok' | 'warn' | 'err';

interface InfraResource {
  id: string;
  type: string;
  name: string;
  subtitle?: string | null;
  status: Status;
  status_label: string;
  badges: string[];
  cost?: string | null;
  provider: string;
  managed: boolean;
  region?: string | null;
  cpu_percent?: number | null;
  memory_percent?: number | null;
  error_title?: string | null;
  error_detail?: string | null;
}

interface InfraMetrics {
  total: number;
  healthy: number;
  issues: number;
  monthly_cost: string;
  managed: number;
}

interface TypeResult {
  resources: InfraResource[];
  metrics: InfraMetrics;
  synced_at: string;
  error?: string | null;
}

interface CreateResourceResponse {
  status: string;
  message: string;
  data: Record<string, unknown>;
}

// ─── Tab definitions ──────────────────────────────────────────────────────────

interface TabDef {
  key: string;           // resource_type param
  label: string;
  icon: string;
  provider: 'aws' | 'azure' | 'all';
  group: 'aws' | 'azure';
}

const TABS: TabDef[] = [
  // AWS
  { key: 'ec2',              label: 'EC2',        icon: '🖥',  provider: 'aws',   group: 'aws' },
  { key: 'ecs_cluster',      label: 'ECS',        icon: '📦',  provider: 'aws',   group: 'aws' },
  { key: 'ecs_service',      label: 'Services',   icon: '⚙️',  provider: 'aws',   group: 'aws' },
  { key: 'ecr',              label: 'ECR',        icon: '🗂',   provider: 'aws',   group: 'aws' },
  { key: 's3',               label: 'S3',         icon: '🪣',  provider: 'aws',   group: 'aws' },
  { key: 'sg',               label: 'Security',   icon: '🔒',  provider: 'aws',   group: 'aws' },
  { key: 'iam',              label: 'IAM',        icon: '👤',  provider: 'aws',   group: 'aws' },
  { key: 'secrets',          label: 'Secrets',    icon: '🗝️',  provider: 'aws',   group: 'aws' },
  { key: 'elb',              label: 'ELB',        icon: '⚖️',  provider: 'aws',   group: 'aws' },
  { key: 'asg',              label: 'ASG',        icon: '📈',  provider: 'aws',   group: 'aws' },
  { key: 'lambda',           label: 'Lambda',     icon: 'λ',   provider: 'aws',   group: 'aws' },
  { key: 'codepipeline',     label: 'Pipeline',   icon: '🔄',  provider: 'aws',   group: 'aws' },
  { key: 'codebuild',        label: 'Build',      icon: '🔨',  provider: 'aws',   group: 'aws' },
  { key: 'codedeploy',       label: 'Deploy',     icon: '📤',  provider: 'aws',   group: 'aws' },
  { key: 'cloudwatch_alarm', label: 'Alarms',     icon: '🔔',  provider: 'aws',   group: 'aws' },
  { key: 'sns',              label: 'SNS',        icon: '📣',  provider: 'aws',   group: 'aws' },
  { key: 'ssm_parameter',    label: 'SSM',        icon: '🗄',   provider: 'aws',   group: 'aws' },
  // Azure
  { key: 'vm',               label: 'VMs',        icon: '💻',  provider: 'azure', group: 'azure' },
  { key: 'acr',              label: 'ACR',        icon: '🗂',   provider: 'azure', group: 'azure' },
  { key: 'keyvault',         label: 'Key Vault',  icon: '🔐',  provider: 'azure', group: 'azure' },
  { key: 'azure_function',   label: 'Functions',  icon: '⚡',  provider: 'azure', group: 'azure' },
  { key: 'azure_lb',         label: 'LB',         icon: '⚖️',  provider: 'azure', group: 'azure' },
  { key: 'azure_nsg',        label: 'NSG',        icon: '🔒',  provider: 'azure', group: 'azure' },
  { key: 'azure_storage',    label: 'Storage',    icon: '💾',  provider: 'azure', group: 'azure' },
];

// ─── Create resource config ───────────────────────────────────────────────────

interface FormField {
  id: string;
  label: string;
  required?: boolean;
  type?: 'text' | 'number' | 'select' | 'textarea' | 'note';
  options?: string[];
  defaultValue?: string;
  placeholder?: string;
  hint?: string;
  noteText?: string;
}

const RESOURCE_CONFIGS: Record<string, { title: string; endpoint: string; fields: FormField[] }> = {
  ec2: { title: 'EC2 Instance', endpoint: '/infrastructure/create/aws/ec2', fields: [
    { id: 'name', label: 'Instance name', required: true, placeholder: 'my-server' },
    { id: 'ami_id', label: 'AMI ID', required: true, placeholder: 'ami-0abcdef1234567890' },
    { id: 'instance_type', label: 'Instance type', type: 'select', options: ['t2.micro','t2.small','t2.medium','t3.micro','t3.small','m5.large'], defaultValue: 't2.micro' },
    { id: 'subnet_id', label: 'Subnet ID', required: true, placeholder: 'subnet-0abc1234' },
    { id: 'security_group_ids', label: 'Security group IDs', required: true, placeholder: 'sg-0abc1234', hint: 'Comma-separated' },
    { id: 'key_name', label: 'Key pair name', placeholder: 'my-key-pair' },
    { id: 'iam_instance_profile', label: 'IAM instance profile', placeholder: 'QixDeployEC2Role' },
  ]},
  ecs_cluster: { title: 'ECS Cluster', endpoint: '/infrastructure/create/aws/ecs/cluster', fields: [
    { id: 'cluster_name', label: 'Cluster name', required: true, placeholder: 'my-cluster' },
  ]},
  ecs_service: { title: 'ECS Service', endpoint: '/infrastructure/create/aws/ecs/service', fields: [
    { id: 'cluster_name', label: 'Cluster name', required: true, placeholder: 'my-cluster' },
    { id: 'service_name', label: 'Service name', required: true, placeholder: 'my-service' },
    { id: 'task_definition', label: 'Task definition', required: true, placeholder: 'my-task:1' },
    { id: 'desired_count', label: 'Desired count', type: 'number', defaultValue: '1' },
    { id: 'launch_type', label: 'Launch type', type: 'select', options: ['FARGATE','EC2'], defaultValue: 'FARGATE' },
    { id: 'subnets', label: 'Subnets', placeholder: 'subnet-abc, subnet-def', hint: 'Comma-separated' },
    { id: 'security_groups', label: 'Security groups', placeholder: 'sg-abc', hint: 'Comma-separated' },
  ]},
  ecr: { title: 'ECR Repository', endpoint: '/infrastructure/create/aws/ecr', fields: [
    { id: 'repository_name', label: 'Repository name', required: true, placeholder: 'my-app-repo' },
    { id: 'image_tag_mutability', label: 'Tag mutability', type: 'select', options: ['MUTABLE','IMMUTABLE'], defaultValue: 'MUTABLE' },
    { id: 'scan_on_push', label: 'Scan on push', type: 'select', options: ['true','false'], defaultValue: 'true' },
  ]},
  s3: { title: 'S3 Bucket', endpoint: '/infrastructure/create/aws/s3', fields: [
    { id: 'bucket_name', label: 'Bucket name', required: true, placeholder: 'qixdeploy-my-bucket', hint: 'Must start with qixdeploy-' },
    { id: 'versioning', label: 'Enable versioning', type: 'select', options: ['false','true'], defaultValue: 'false' },
  ]},
  secret: { title: 'Secret', endpoint: '/infrastructure/create/aws/secret', fields: [
    { id: 'name', label: 'Secret name', required: true, placeholder: 'my-app/prod' },
    { id: 'secret_keys', label: 'Key=Value pairs', required: true, type: 'textarea', placeholder: 'DB_HOST=mydb.example.com\nDB_PASS=secret', hint: 'One per line' },
  ]},
  lambda: { title: 'Lambda Function', endpoint: '/infrastructure/create/aws/lambda', fields: [
    { id: 'function_name', label: 'Function name', required: true, placeholder: 'qixdeploy-my-fn', hint: 'Must start with qixdeploy-' },
    { id: 'runtime', label: 'Runtime', type: 'select', options: ['python3.11','python3.10','nodejs20.x','nodejs18.x'], defaultValue: 'python3.11' },
    { id: 'handler', label: 'Handler', required: true, placeholder: 'lambda_function.lambda_handler' },
    { id: 'role_arn', label: 'Execution role ARN', required: true, placeholder: 'arn:aws:iam::…:role/my-role' },
    { id: 'memory_size', label: 'Memory (MB)', type: 'number', defaultValue: '128' },
    { id: 'timeout', label: 'Timeout (sec)', type: 'number', defaultValue: '30' },
  ]},
  elb: { title: 'Load Balancer', endpoint: '/infrastructure/create/aws/elb', fields: [
    { id: 'name', label: 'Name', required: true, placeholder: 'my-alb' },
    { id: 'lb_type', label: 'Type', type: 'select', options: ['application','network'], defaultValue: 'application' },
    { id: 'scheme', label: 'Scheme', type: 'select', options: ['internet-facing','internal'], defaultValue: 'internet-facing' },
    { id: 'subnets', label: 'Subnets', required: true, placeholder: 'subnet-abc, subnet-def', hint: 'Comma-separated, min 2' },
    { id: 'security_groups', label: 'Security groups (ALB)', placeholder: 'sg-abc', hint: 'Comma-separated' },
  ]},
  asg: { title: 'Auto Scaling Group', endpoint: '/infrastructure/create/aws/asg', fields: [
    { id: 'asg_name', label: 'ASG name', required: true, placeholder: 'my-asg' },
    { id: 'launch_template_id', label: 'Launch template ID', required: true, placeholder: 'lt-0abc1234' },
    { id: 'min_size', label: 'Min size', type: 'number', defaultValue: '1' },
    { id: 'max_size', label: 'Max size', type: 'number', defaultValue: '3' },
    { id: 'desired_capacity', label: 'Desired', type: 'number', defaultValue: '1' },
    { id: 'subnets', label: 'Subnets', required: true, placeholder: 'subnet-abc, subnet-def', hint: 'Comma-separated' },
  ]},
  codepipeline: { title: 'CodePipeline', endpoint: '/infrastructure/create/aws/codepipeline', fields: [
    { id: 'pipeline_name', label: 'Pipeline name', required: true, placeholder: 'my-pipeline' },
    { id: 'role_arn', label: 'Role ARN', required: true, placeholder: 'arn:aws:iam::…:role/CodePipelineRole' },
    { id: 'artifact_bucket', label: 'Artifact bucket', required: true, placeholder: 'qixdeploy-artifacts' },
    { id: 'source_connection_arn', label: 'CodeStar connection ARN', required: true, placeholder: 'arn:aws:codestar-connections:…' },
    { id: 'source_repo', label: 'Repo (owner/repo)', required: true, placeholder: 'myorg/my-repo' },
    { id: 'source_branch', label: 'Branch', defaultValue: 'main' },
    { id: 'codebuild_project_name', label: 'CodeBuild project', required: true, placeholder: 'my-build' },
  ]},
  codebuild: { title: 'CodeBuild Project', endpoint: '/infrastructure/create/aws/codebuild', fields: [
    { id: 'project_name', label: 'Project name', required: true, placeholder: 'my-build-project' },
    { id: 'service_role_arn', label: 'Service role ARN', required: true, placeholder: 'arn:aws:iam::…:role/CodeBuildRole' },
    { id: 'artifact_bucket', label: 'Artifact bucket', required: true, placeholder: 'qixdeploy-artifacts' },
    { id: 'image', label: 'Image', type: 'select', options: ['aws/codebuild/standard:7.0','aws/codebuild/standard:6.0'], defaultValue: 'aws/codebuild/standard:7.0' },
    { id: 'compute_type', label: 'Compute', type: 'select', options: ['BUILD_GENERAL1_SMALL','BUILD_GENERAL1_MEDIUM','BUILD_GENERAL1_LARGE'], defaultValue: 'BUILD_GENERAL1_SMALL' },
  ]},
  sns: { title: 'SNS Topic', endpoint: '/infrastructure/create/aws/sns', fields: [
    { id: 'topic_name', label: 'Topic name', required: true, placeholder: 'qixdeploy-alerts', hint: 'Must match qixdeploy*, *-alerts, monitoring*' },
    { id: 'email_subscription', label: 'Email subscription (optional)', placeholder: 'alerts@mycompany.com' },
  ]},
  cloudwatch_alarm: { title: 'CloudWatch Alarm', endpoint: '/infrastructure/create/aws/cloudwatch/alarm', fields: [
    { id: 'alarm_name', label: 'Alarm name', required: true, placeholder: 'high-cpu-alarm' },
    { id: 'metric_name', label: 'Metric', required: true, placeholder: 'CPUUtilization' },
    { id: 'namespace', label: 'Namespace', defaultValue: 'AWS/EC2' },
    { id: 'threshold', label: 'Threshold', type: 'number', defaultValue: '80' },
    { id: 'comparison_operator', label: 'Comparison', type: 'select', options: ['GreaterThanOrEqualToThreshold','GreaterThanThreshold','LessThanThreshold','LessThanOrEqualToThreshold'], defaultValue: 'GreaterThanOrEqualToThreshold' },
  ]},
  ssm_parameter: { title: 'SSM Parameter', endpoint: '/infrastructure/create/aws/ssm', fields: [
    { id: 'name', label: 'Parameter name', required: true, placeholder: '/qixdeploy/my-param', hint: 'Must start with /qixdeploy/' },
    { id: 'value', label: 'Value', required: true, placeholder: 'my-value' },
    { id: 'param_type', label: 'Type', type: 'select', options: ['SecureString','String','StringList'], defaultValue: 'SecureString' },
  ]},
  vm: { title: 'Azure VM', endpoint: '/infrastructure/create/azure/vm', fields: [
    { id: 'name', label: 'VM name', required: true, placeholder: 'my-ubuntu-vm' },
    { id: 'resource_group', label: 'Resource group', required: true, placeholder: 'my-rg' },
    { id: 'vm_size', label: 'VM size', type: 'select', options: ['Standard_B1s','Standard_B2s','Standard_B4ms','Standard_D2s_v3'], defaultValue: 'Standard_B2s' },
    { id: 'admin_username', label: 'Admin username', defaultValue: 'azureuser' },
    { id: 'ssh_public_key', label: 'SSH public key', required: true, type: 'textarea', placeholder: 'ssh-rsa AAAA…' },
    { id: 'vnet_name', label: 'VNet name', required: true, placeholder: 'my-vnet' },
    { id: 'subnet_name', label: 'Subnet', required: true, placeholder: 'default' },
  ]},
  acr: { title: 'Azure Container Registry', endpoint: '/infrastructure/create/azure/acr', fields: [
    { id: 'registry_name', label: 'Registry name', required: true, placeholder: 'myregistryname' },
    { id: 'resource_group', label: 'Resource group', required: true, placeholder: 'my-rg' },
    { id: 'sku', label: 'SKU', type: 'select', options: ['Basic','Standard','Premium'], defaultValue: 'Basic' },
  ]},
  keyvault: { title: 'Azure Key Vault', endpoint: '/infrastructure/create/azure/keyvault', fields: [
    { id: 'vault_name', label: 'Vault name', required: true, placeholder: 'my-keyvault' },
    { id: 'resource_group', label: 'Resource group', required: true, placeholder: 'my-rg' },
    { id: 'sku', label: 'SKU', type: 'select', options: ['standard','premium'], defaultValue: 'standard' },
  ]},
  azure_nsg: { title: 'Azure NSG', endpoint: '/infrastructure/create/azure/nsg', fields: [
    { id: 'nsg_name', label: 'NSG name', required: true, placeholder: 'my-nsg' },
    { id: 'resource_group', label: 'Resource group', required: true, placeholder: 'my-rg' },
  ]},
  azure_storage: { title: 'Azure Storage Account', endpoint: '/infrastructure/create/azure/storage', fields: [
    { id: 'account_name', label: 'Account name', required: true, placeholder: 'qixdeploystorage', hint: 'Must start with qixdeploy' },
    { id: 'resource_group', label: 'Resource group', required: true, placeholder: 'my-rg' },
    { id: 'sku', label: 'SKU', type: 'select', options: ['Standard_LRS','Standard_GRS','Premium_LRS'], defaultValue: 'Standard_LRS' },
  ]},
};

// ─── Payload builder ──────────────────────────────────────────────────────────

function buildPayload(typeKey: string, values: Record<string, string>): Record<string, unknown> {
  const v = (id: string) => values[id] ?? '';
  const arr = (id: string) => v(id).split(',').map(s => s.trim()).filter(Boolean);
  const tab = TABS.find(t => t.key === typeKey);
  const clientKey = tab?.group === 'azure' ? { azure_client_id: 1 } : { aws_client_id: 1 };

  const map: Record<string, Record<string, unknown>> = {
    ec2: { ...clientKey, name: v('name'), ami_id: v('ami_id'), instance_type: v('instance_type') || 't2.micro', subnet_id: v('subnet_id'), security_group_ids: arr('security_group_ids'), key_name: v('key_name') || null, iam_instance_profile: v('iam_instance_profile') || null },
    ecs_cluster: { ...clientKey, cluster_name: v('cluster_name') },
    ecs_service: { ...clientKey, cluster_name: v('cluster_name'), service_name: v('service_name'), task_definition: v('task_definition'), desired_count: parseInt(v('desired_count')) || 1, launch_type: v('launch_type') || 'FARGATE', subnets: arr('subnets'), security_groups: arr('security_groups') },
    ecr: { ...clientKey, repository_name: v('repository_name'), image_tag_mutability: v('image_tag_mutability') || 'MUTABLE', scan_on_push: v('scan_on_push') === 'true' },
    s3: { ...clientKey, bucket_name: v('bucket_name'), versioning: v('versioning') === 'true' },
    secret: { ...clientKey, name: v('name'), secret_value: Object.fromEntries((v('secret_keys') || '').split('\n').filter(l => l.includes('=')).map(l => [l.split('=')[0].trim(), l.split('=').slice(1).join('=').trim()])) },
    lambda: { ...clientKey, function_name: v('function_name'), runtime: v('runtime') || 'python3.11', handler: v('handler'), role_arn: v('role_arn'), memory_size: parseInt(v('memory_size')) || 128, timeout: parseInt(v('timeout')) || 30, zip_file_base64: 'UEsFBgAAAAAAAAAAAAAAAAAAAAAAAA==' },
    elb: { ...clientKey, name: v('name'), lb_type: v('lb_type') || 'application', scheme: v('scheme') || 'internet-facing', subnets: arr('subnets'), security_groups: arr('security_groups') },
    asg: { ...clientKey, asg_name: v('asg_name'), launch_template_id: v('launch_template_id'), min_size: parseInt(v('min_size')) || 1, max_size: parseInt(v('max_size')) || 3, desired_capacity: parseInt(v('desired_capacity')) || 1, subnets: arr('subnets') },
    codepipeline: { ...clientKey, pipeline_name: v('pipeline_name'), role_arn: v('role_arn'), artifact_bucket: v('artifact_bucket'), source_connection_arn: v('source_connection_arn'), source_repo: v('source_repo'), source_branch: v('source_branch') || 'main', codebuild_project_name: v('codebuild_project_name') },
    codebuild: { ...clientKey, project_name: v('project_name'), service_role_arn: v('service_role_arn'), artifact_bucket: v('artifact_bucket'), image: v('image') || 'aws/codebuild/standard:7.0', compute_type: v('compute_type') || 'BUILD_GENERAL1_SMALL' },
    sns: { ...clientKey, topic_name: v('topic_name'), email_subscription: v('email_subscription') || null },
    cloudwatch_alarm: { ...clientKey, alarm_name: v('alarm_name'), metric_name: v('metric_name'), namespace: v('namespace') || 'AWS/EC2', threshold: parseFloat(v('threshold')) || 80, comparison_operator: v('comparison_operator') || 'GreaterThanOrEqualToThreshold' },
    ssm_parameter: { ...clientKey, name: v('name'), value: v('value'), param_type: v('param_type') || 'SecureString' },
    vm: { ...clientKey, name: v('name'), resource_group: v('resource_group'), vm_size: v('vm_size') || 'Standard_B2s', admin_username: v('admin_username') || 'azureuser', ssh_public_key: v('ssh_public_key'), vnet_name: v('vnet_name'), subnet_name: v('subnet_name') },
    acr: { ...clientKey, registry_name: v('registry_name'), resource_group: v('resource_group'), sku: v('sku') || 'Basic' },
    keyvault: { ...clientKey, vault_name: v('vault_name'), resource_group: v('resource_group'), sku: v('sku') || 'standard' },
    azure_nsg: { ...clientKey, nsg_name: v('nsg_name'), resource_group: v('resource_group') },
    azure_storage: { ...clientKey, account_name: v('account_name'), resource_group: v('resource_group'), sku: v('sku') || 'Standard_LRS' },
  };
  return map[typeKey] ?? clientKey;
}

// ─── Styles (same pattern as original InfrastructureView) ────────────────────

const statusColors: Record<Status, string> = { ok: '#22c55e', warn: '#eab308', err: '#f43f5e' };
const statusBg: Record<Status, string> = { ok: 'rgba(34,197,94,.06)', warn: 'rgba(234,179,8,.06)', err: 'rgba(244,63,94,.06)' };
const statusBorder: Record<Status, string> = { ok: 'rgba(34,197,94,.2)', warn: 'rgba(234,179,8,.2)', err: 'rgba(244,63,94,.3)' };

const S = {
  page: { background: '#09090e', minHeight: '100vh', fontFamily: "'Inter',sans-serif" } as React.CSSProperties,
  topbar: { height: '50px', background: '#111116', borderBottom: '1px solid #26262f', display: 'flex', alignItems: 'center', padding: '0 20px', gap: '10px', position: 'sticky' as const, top: 0, zIndex: 50 } as React.CSSProperties,
  bc: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', color: '#8e8ea8' } as React.CSSProperties,
  content: { padding: '20px 24px' } as React.CSSProperties,
  pageTitle: { fontFamily: "'Syne',sans-serif", fontSize: '20px', fontWeight: 700, color: '#eeeef5', marginBottom: '3px' } as React.CSSProperties,
  pageSub: { fontSize: '13px', color: '#8e8ea8', marginBottom: '20px' } as React.CSSProperties,

  btn: (v: 'primary' | 'secondary' | 'danger' | 'warn' = 'secondary'): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    padding: '6px 12px', borderRadius: '6px', fontSize: '12.5px', fontWeight: 500,
    cursor: 'pointer', border: 'none', fontFamily: "'Inter',sans-serif", transition: 'all .15s',
    ...(v === 'primary' ? { background: '#6366f1', color: '#fff' }
      : v === 'danger' ? { background: 'rgba(244,63,94,.1)', color: '#f43f5e', border: '1px solid rgba(244,63,94,.2)' }
      : v === 'warn' ? { background: 'rgba(234,179,8,.1)', color: '#eab308', border: '1px solid rgba(234,179,8,.2)' }
      : { background: '#17171e', color: '#eeeef5', border: '1px solid #32323d' }),
  }),

  badge: (v: 'ok'|'err'|'warn'|'neu'|'purple'|'blue' = 'neu'): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '2px 7px',
    borderRadius: '20px', fontSize: '10.5px', fontWeight: 600,
    ...(v === 'ok' ? { background: 'rgba(34,197,94,.1)', color: '#22c55e' }
      : v === 'err' ? { background: 'rgba(244,63,94,.1)', color: '#f43f5e' }
      : v === 'warn' ? { background: 'rgba(234,179,8,.1)', color: '#eab308' }
      : v === 'purple' ? { background: 'rgba(99,102,241,.14)', color: '#818cf8' }
      : v === 'blue' ? { background: 'rgba(59,130,246,.1)', color: '#3b82f6' }
      : { background: '#1e1e27', color: '#8e8ea8', border: '1px solid #26262f' }),
  }),

  dot: (color: string): React.CSSProperties => ({ width: '5px', height: '5px', borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block' }),

  input: { width: '100%', padding: '7px 10px', borderRadius: '6px', border: '1px solid #32323d', background: '#17171e', color: '#eeeef5', fontSize: '13px', fontFamily: "'Inter',sans-serif", outline: 'none', boxSizing: 'border-box' as const } as React.CSSProperties,
};

// ─── Spinner ──────────────────────────────────────────────────────────────────

const Spinner: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <div style={{ width: size, height: size, borderRadius: '50%', border: '2px solid #32323d', borderTopColor: '#6366f1', animation: 'spin .7s linear infinite', flexShrink: 0 }} />
);

// ─── Resource card (identical to original) ───────────────────────────────────

const ResourceCard: React.FC<{ r: InfraResource }> = ({ r }) => {
  const status = r.status as Status;
  const provBadge = r.provider === 'AWS'
    ? <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 5px', borderRadius: '3px', background: 'rgba(255,153,0,.1)', color: '#ff9900' }}>AWS</span>
    : <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 5px', borderRadius: '3px', background: 'rgba(0,120,212,.1)', color: '#3b82f6' }}>Azure</span>;

  return (
    <div style={{ background: '#111116', border: `1px solid ${statusBorder[status]}`, backgroundColor: statusBg[status], borderRadius: '10px', padding: '14px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '10px', position: 'relative', transition: 'all .2s' }}>
      {r.managed && <div style={{ position: 'absolute', top: '10px', right: '10px' }}><span style={S.badge('purple')}>QixDeploy</span></div>}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: statusBg[status], border: `1px solid ${statusBorder[status]}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', flexShrink: 0 }}>
          {TABS.find(t => t.key === r.type)?.icon ?? '☁'}
        </div>
        <div style={{ flex: 1, paddingRight: r.managed ? '56px' : '0', minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#eeeef5', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '3px' }}>{r.name}</div>
          <div style={{ fontSize: '10.5px', color: '#8e8ea8', fontFamily: "'IBM Plex Mono',monospace", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.subtitle || r.id}</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
        <span style={{ ...S.badge(status), display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
          <span style={S.dot(statusColors[status])} />{r.status_label}
        </span>
        {provBadge}
        {(r.badges || []).map(b => <span key={b} style={S.badge('neu')}>{b}</span>)}
      </div>
      {(r.cpu_percent != null || r.memory_percent != null) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', background: '#17171e', borderRadius: '6px', padding: '10px' }}>
          {r.cpu_percent != null && (
            <div>
              <div style={{ fontSize: '10px', color: '#52525f', marginBottom: '3px' }}>CPU</div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: r.cpu_percent > 70 ? '#eab308' : '#22c55e' }}>{r.cpu_percent}%</div>
              <div style={{ height: '3px', background: '#25252f', borderRadius: '2px', marginTop: '3px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${r.cpu_percent}%`, background: r.cpu_percent > 70 ? 'linear-gradient(90deg,#eab308,#f97316)' : 'linear-gradient(90deg,#22c55e,#06b6d4)', borderRadius: '2px' }} />
              </div>
            </div>
          )}
          {r.memory_percent != null && (
            <div>
              <div style={{ fontSize: '10px', color: '#52525f', marginBottom: '3px' }}>Memory</div>
              <div style={{ fontSize: '13px', fontWeight: 700 }}>{r.memory_percent}%</div>
              <div style={{ height: '3px', background: '#25252f', borderRadius: '2px', marginTop: '3px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${r.memory_percent}%`, background: 'linear-gradient(90deg,#6366f1,#a855f7)', borderRadius: '2px' }} />
              </div>
            </div>
          )}
        </div>
      )}
      {r.error_title && (
        <div style={{ background: 'rgba(244,63,94,.08)', border: '1px solid rgba(244,63,94,.2)', borderRadius: '6px', padding: '9px 10px' }}>
          <div style={{ fontSize: '10.5px', color: '#f43f5e', fontWeight: 600, marginBottom: '3px' }}>{r.error_title}</div>
          <div style={{ fontSize: '11px', color: '#8e8ea8', fontFamily: "'IBM Plex Mono',monospace" }}>{r.error_detail}</div>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid #26262f' }}>
        <div>
          <div style={{ fontSize: '12.5px', fontWeight: 700, color: '#eeeef5' }}>{r.cost || '—'}</div>
          <div style={{ fontSize: '10px', color: '#52525f' }}>this month</div>
        </div>
        {r.status === 'err' && (
          <div style={{ display: 'flex', gap: '5px' }}>
            <button style={{ ...S.btn('warn'), fontSize: '11px', padding: '3px 8px' }}>🔄 Retry</button>
            <button style={{ ...S.btn('secondary'), fontSize: '11px', padding: '3px 8px' }}>🤖 AI Fix</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Create modal ─────────────────────────────────────────────────────────────

const CreateModal: React.FC<{ activeTab: string; onClose: () => void; onSuccess: () => void }> = ({ activeTab, onClose, onSuccess }) => {
  // Default to the current tab's create config if available, else ec2
  const defaultType = RESOURCE_CONFIGS[activeTab] ? activeTab : 'ec2';
  const [selectedType, setSelectedType] = useState(defaultType);
  const [values, setValues] = useState<Record<string, string>>(() => {
    const defaults: Record<string, string> = {};
    RESOURCE_CONFIGS[defaultType]?.fields.forEach(f => { if (f.defaultValue) defaults[f.id] = f.defaultValue; });
    return defaults;
  });
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ msg: string; type: 'success'|'error' } | null>(null);

  const config = RESOURCE_CONFIGS[selectedType];

  const changeType = (key: string) => {
    setSelectedType(key);
    const defaults: Record<string, string> = {};
    RESOURCE_CONFIGS[key]?.fields.forEach(f => { if (f.defaultValue) defaults[f.id] = f.defaultValue; });
    setValues(defaults);
    setFeedback(null);
  };

  const setField = (id: string, v: string) => setValues(prev => ({ ...prev, [id]: v }));

  const submit = async () => {
    if (submitting || !config) return;
    setSubmitting(true);
    setFeedback(null);
    try {
      const res = await axiosInstance.post(config.endpoint, buildPayload(selectedType, values));
      const d: CreateResourceResponse = res.data;
      if (d.status === 'error') throw new Error(d.message);
      setFeedback({ msg: d.message || 'Created successfully', type: 'success' });
      setTimeout(() => { onClose(); onSuccess(); }, 1400);
    } catch (err: any) {
      setFeedback({ msg: err?.response?.data?.detail || err?.message || 'Creation failed', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const awsTypes = TABS.filter(t => t.group === 'aws').map(t => ({ key: t.key, icon: t.icon, name: t.label }));
  const azureTypes = TABS.filter(t => t.group === 'azure').map(t => ({ key: t.key, icon: t.icon, name: t.label }));
  const currentGroup = TABS.find(t => t.key === selectedType)?.group ?? 'aws';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)', backdropFilter: 'blur(3px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={onClose}>
      <div style={{ background: '#111116', border: '1px solid #26262f', borderRadius: '12px', width: '100%', maxWidth: '560px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #26262f', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <span style={{ fontFamily: "'Syne',sans-serif", fontSize: '15px', fontWeight: 700, color: '#eeeef5' }}>Add Resource</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#52525f', fontSize: '18px' }}>×</button>
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '18px' }}>

          {/* Provider tabs */}
          <div>
            <div style={{ fontSize: '11px', color: '#52525f', fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: '10px', paddingBottom: '6px', borderBottom: '1px solid #26262f' }}>Cloud provider</div>
            <div style={{ display: 'flex', gap: '6px' }}>
              {(['aws', 'azure'] as const).map(p => (
                <button key={p} onClick={() => changeType(p === 'aws' ? 'ec2' : 'vm')}
                  style={{ padding: '4px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 500, cursor: 'pointer', border: currentGroup === p ? '1px solid rgba(99,102,241,.3)' : '1px solid #26262f', background: currentGroup === p ? 'rgba(99,102,241,.1)' : '#17171e', color: currentGroup === p ? '#818cf8' : '#8e8ea8' }}>
                  {p.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Type grid */}
          <div>
            <div style={{ fontSize: '11px', color: '#52525f', fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: '10px', paddingBottom: '6px', borderBottom: '1px solid #26262f' }}>Resource type</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '8px' }}>
              {(currentGroup === 'aws' ? awsTypes : azureTypes).map(t => (
                <div key={t.key} onClick={() => changeType(t.key)} style={{ padding: '10px 8px', border: selectedType === t.key ? '1px solid rgba(99,102,241,.4)' : '1px solid #26262f', borderRadius: '8px', cursor: 'pointer', textAlign: 'center', background: selectedType === t.key ? 'rgba(99,102,241,.08)' : '#17171e', transition: 'all .15s' }}>
                  <div style={{ fontSize: '15px', marginBottom: '3px' }}>{t.icon}</div>
                  <div style={{ fontSize: '10.5px', fontWeight: 600, color: '#eeeef5' }}>{t.name}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          {config && (
            <div>
              <div style={{ fontSize: '11px', color: '#52525f', fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: '10px', paddingBottom: '6px', borderBottom: '1px solid #26262f' }}>{config.title} configuration</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {config.fields.map(f => {
                  if (f.type === 'note') return <div key={f.id} style={{ fontSize: '12px', color: '#8e8ea8', padding: '8px 10px', background: '#17171e', borderRadius: '6px', border: '1px solid #26262f' }}>{f.noteText}</div>;
                  const val = values[f.id] ?? '';
                  return (
                    <div key={f.id}>
                      <label style={{ fontSize: '12px', fontWeight: 500, color: '#8e8ea8', display: 'block', marginBottom: '5px' }}>
                        {f.label}{f.required && <span style={{ color: '#f43f5e', marginLeft: '2px' }}>*</span>}
                      </label>
                      {f.type === 'select' ? (
                        <select value={val} onChange={e => setField(f.id, e.target.value)} style={{ ...S.input, appearance: 'none' as any }}>
                          {(f.options ?? []).map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : f.type === 'textarea' ? (
                        <textarea value={val} placeholder={f.placeholder} onChange={e => setField(f.id, e.target.value)} rows={3} style={{ ...S.input, resize: 'vertical', minHeight: '70px' }} />
                      ) : (
                        <input type={f.type || 'text'} value={val} placeholder={f.placeholder} onChange={e => setField(f.id, e.target.value)} style={S.input} />
                      )}
                      {f.hint && <div style={{ fontSize: '11px', color: '#52525f', marginTop: '3px' }}>{f.hint}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {feedback && (
            <div style={{ padding: '9px 12px', borderRadius: '6px', fontSize: '12.5px', background: feedback.type === 'success' ? 'rgba(34,197,94,.08)' : 'rgba(244,63,94,.08)', border: `1px solid ${feedback.type === 'success' ? 'rgba(34,197,94,.25)' : 'rgba(244,63,94,.25)'}`, color: feedback.type === 'success' ? '#22c55e' : '#f43f5e' }}>
              {feedback.msg}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid #26262f', display: 'flex', gap: '8px', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button style={S.btn('secondary')} onClick={onClose}>Cancel</button>
          <button style={{ ...S.btn('primary'), opacity: submitting ? 0.7 : 1 }} onClick={submit} disabled={submitting}>
            {submitting && <Spinner size={11} />}
            {submitting ? 'Creating…' : 'Create Resource'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main view ────────────────────────────────────────────────────────────────

const InfrastructureView: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>(TABS[0].key);
  // Cache: key = resource_type, value = last fetched TypeResult
  const cache = useRef<Record<string, TypeResult>>({});
  const [tabData, setTabData] = useState<TypeResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  // Track which tabs were visited — show a "·" badge on unvisited tabs
  const visited = useRef<Set<string>>(new Set());

  // Aggregate metrics across all cached tabs
  const aggregateMetrics = (): { total: number; healthy: number; issues: number } => {
    let total = 0, healthy = 0, issues = 0;
    Object.values(cache.current).forEach(d => {
      total += d.metrics.total;
      healthy += d.metrics.healthy;
      issues += d.metrics.issues;
    });
    return { total, healthy, issues };
  };
  const [agg, setAgg] = useState({ total: 0, healthy: 0, issues: 0 });

  const fetchTab = useCallback(async (tabKey: string, forceRefresh: boolean = false) => {
  setLoading(true);
  const tab = TABS.find(t => t.key === tabKey)!;

  try {
    const url = `/infrastructure/resources?resource_type=${tabKey}&provider=${tab.provider}`;
    const finalUrl = forceRefresh ? `${url}&refresh=true` : url;

    const res = await axiosInstance.get(finalUrl);
    const d = res.data;

    const result: TypeResult = {
      resources: d.data?.resources ?? [],
      metrics: d.data?.metrics ?? { total: 0, healthy: 0, issues: 0, monthly_cost: '—', managed: 0 },
      synced_at: d.data?.synced_at ?? '',
      error: null,
    };

    cache.current[tabKey] = result;
    visited.current.add(tabKey);
    setTabData(result);
    setAgg(aggregateMetrics());
  } catch (err: any) {
    const errResult: TypeResult = {
      resources: [],
      metrics: { total: 0, healthy: 0, issues: 0, monthly_cost: '—', managed: 0 },
      synced_at: '',
      error: err?.response?.data?.detail || err?.message || 'Failed',
    };
    cache.current[tabKey] = errResult;
    setTabData(errResult);
  } finally {
    setLoading(false);
  }
}, []);

  const handleTabClick = (key: string) => {
    setActiveTab(key);
    // Show cached data immediately if available
    if (cache.current[key]) {
      setTabData(cache.current[key]);
    }
    fetchTab(key, false);   // normal load (uses cache)
  };

  const handleSync = () => {
  fetchTab(activeTab, true);   // forceRefresh = true
  };
  // Load first tab on mount
  React.useEffect(() => {
    fetchTab(TABS[0].key);
  }, []);

  React.useEffect(() => {
    fetchTab(TABS[0].key, false);
  }, []);


  const handleRefresh = () => fetchTab(activeTab, true);

  const syncLabel = (() => {
    if (loading) return 'Loading…';
    if (!tabData?.synced_at) return 'Not synced';
    const mins = Math.round((Date.now() - new Date(tabData.synced_at).getTime()) / 60000);
    return `Synced ${mins < 1 ? 'just now' : `${mins}m ago`}`;
  })();

  const awsTabs = TABS.filter(t => t.group === 'aws');
  const azureTabs = TABS.filter(t => t.group === 'azure');

  const errorResources = (tabData?.resources ?? []).filter(r => r.status === 'err');

  return (
    <div style={S.page}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Top bar */}
      <div style={S.topbar}>
        <div style={S.bc}>
          <span>Home</span><span style={{ color: '#52525f' }}>›</span>
          <span style={{ color: '#eeeef5', fontWeight: 500 }}>Infrastructure</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ fontSize: '11px', color: '#52525f', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <button 
            style={S.btn('secondary')} 
              onClick={handleSync} 
              disabled={loading}
            >
              {loading ? <Spinner size={11} /> : null}
              {loading ? 'Loading…' : 'Sync'}
            </button>
          </span>
          <button style={S.btn('secondary')} onClick={handleRefresh} disabled={loading}>
            {loading ? <Spinner size={11} /> : null}
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          {/* <button style={S.btn('primary')} onClick={() => setShowCreate(true)}>+ Add Resource</button> */}
        </div>
      </div>

      <div style={S.content}>
        {/* Header */}
        <div style={{ marginBottom: '20px' }}>
          <h1 style={S.pageTitle}>Infrastructure</h1>
          <p style={S.pageSub}>All AWS and Azure resources provisioned across your deployments</p>
        </div>

        {/* Aggregate stats (built from cache as tabs are visited) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: '10px', marginBottom: '20px' }}>
          {[
            { label: 'Total (loaded)', value: agg.total || '—', sub: 'across loaded tabs', color: '#eeeef5' },
            { label: 'Healthy', value: agg.healthy || '—', sub: 'running normally', color: '#22c55e' },
            { label: 'Issues', value: agg.issues || '—', sub: 'needs attention', color: '#f43f5e' },
            { label: 'This tab', value: tabData?.metrics.total ?? '—', sub: TABS.find(t => t.key === activeTab)?.label ?? '', color: '#818cf8' },
            { label: 'This tab cost', value: tabData?.metrics.monthly_cost ?? '—', sub: 'this month', color: '#eeeef5' },
          ].map(stat => (
            <div key={stat.label} style={{ background: '#111116', border: '1px solid #26262f', borderRadius: '10px', padding: '12px 14px' }}>
              <div style={{ fontSize: '10.5px', color: '#52525f', fontWeight: 500, marginBottom: '5px' }}>{stat.label}</div>
              <div style={{ fontSize: '20px', fontWeight: 700, fontFamily: "'Syne',sans-serif", color: stat.color, lineHeight: 1 }}>{stat.value}</div>
              <div style={{ fontSize: '10.5px', color: stat.color === '#eeeef5' ? '#52525f' : stat.color, marginTop: '3px' }}>{stat.sub}</div>
            </div>
          ))}
        </div>

        {/* Error alerts for current tab */}
        {errorResources.map(r => (
          <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 12px', borderRadius: '6px', fontSize: '12px', background: 'rgba(244,63,94,.1)', border: '1px solid rgba(244,63,94,.25)', color: '#f43f5e', marginBottom: '8px' }}>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="6.5" cy="6.5" r="5.5" /><path d="M6.5 3.5v3M6.5 9v.5" /></svg>
            <strong>{r.name}</strong>{r.error_detail ? ` — ${r.error_detail}` : ''}
            {/* <button style={{ ...S.btn('danger'), marginLeft: 'auto', fontSize: '11.5px', padding: '3px 9px' }}>View & Fix →</button> */}
          </div>
        ))}

        {/* AWS Tab group */}
        <div style={{ marginBottom: '4px' }}>
          <div style={{ fontSize: '10px', color: '#52525f', fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px' }}>🟠</span> AWS
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap', marginBottom: '10px' }}>
            {awsTabs.map(tab => {
              const isActive = activeTab === tab.key;
              const isCached = !!cache.current[tab.key];
              const hasIssue = isCached && cache.current[tab.key].metrics.issues > 0;
              return (
                <button key={tab.key} onClick={() => handleTabClick(tab.key)} style={{
                  display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px',
                  borderRadius: '20px', fontSize: '11.5px', fontWeight: 500, cursor: 'pointer', transition: 'all .15s',
                  background: isActive ? 'rgba(99,102,241,.14)' : '#17171e',
                  border: `1px solid ${isActive ? 'rgba(99,102,241,.3)' : '#26262f'}`,
                  color: isActive ? '#818cf8' : '#8e8ea8',
                  position: 'relative',
                }}>
                  <span style={{ fontSize: '12px' }}>{tab.icon}</span>
                  {tab.label}
                  {isCached && cache.current[tab.key].metrics.total > 0 && (
                    <span style={{ fontSize: '9px', padding: '0 4px', borderRadius: '10px', background: hasIssue ? 'rgba(244,63,94,.2)' : 'rgba(34,197,94,.12)', color: hasIssue ? '#f43f5e' : '#22c55e', fontWeight: 700 }}>
                      {cache.current[tab.key].metrics.total}
                    </span>
                  )}
                  {loading && isActive && <Spinner size={9} />}
                </button>
              );
            })}
          </div>

          {/* Azure Tab group */}
          <div style={{ fontSize: '10px', color: '#52525f', fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px' }}>🔷</span> Azure
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap', marginBottom: '18px' }}>
            {azureTabs.map(tab => {
              const isActive = activeTab === tab.key;
              const isCached = !!cache.current[tab.key];
              const hasIssue = isCached && cache.current[tab.key].metrics.issues > 0;
              return (
                <button key={tab.key} onClick={() => handleTabClick(tab.key)} style={{
                  display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px',
                  borderRadius: '20px', fontSize: '11.5px', fontWeight: 500, cursor: 'pointer', transition: 'all .15s',
                  background: isActive ? 'rgba(59,130,246,.14)' : '#17171e',
                  border: `1px solid ${isActive ? 'rgba(59,130,246,.3)' : '#26262f'}`,
                  color: isActive ? '#3b82f6' : '#8e8ea8',
                }}>
                  <span style={{ fontSize: '12px' }}>{tab.icon}</span>
                  {tab.label}
                  {isCached && cache.current[tab.key].metrics.total > 0 && (
                    <span style={{ fontSize: '9px', padding: '0 4px', borderRadius: '10px', background: hasIssue ? 'rgba(244,63,94,.2)' : 'rgba(34,197,94,.12)', color: hasIssue ? '#f43f5e' : '#22c55e', fontWeight: 700 }}>
                      {cache.current[tab.key].metrics.total}
                    </span>
                  )}
                  {loading && isActive && <Spinner size={9} />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab content */}
        {loading && !tabData ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 0', gap: '12px', color: '#8e8ea8', fontSize: '13px' }}>
            <Spinner size={24} />
            <span>Fetching {TABS.find(t => t.key === activeTab)?.label}…</span>
          </div>
        ) : tabData?.error ? (
          <div style={{ textAlign: 'center', padding: '50px 0', color: '#8e8ea8' }}>
            <div style={{ fontSize: '28px', marginBottom: '10px' }}>⚠</div>
            <div style={{ fontSize: '13px', color: '#eeeef5', marginBottom: '4px' }}>Failed to load</div>
            <div style={{ fontSize: '12px', color: '#52525f', marginBottom: '14px' }}>{tabData.error}</div>
            <button style={S.btn('secondary')} onClick={handleRefresh}>Retry</button>
          </div>
        ) : !tabData || tabData.resources.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px 0', color: '#8e8ea8' }}>
            <div style={{ fontSize: '28px', marginBottom: '10px' }}>
              {loading ? '⏳' : TABS.find(t => t.key === activeTab)?.icon ?? '☁'}
            </div>
            {loading ? (
              <>
                <div style={{ fontSize: '13px', color: '#eeeef5', marginBottom: '4px' }}>Fetching {TABS.find(t => t.key === activeTab)?.label}…</div>
                <div style={{ fontSize: '11.5px', color: '#52525f' }}>This usually takes 2–5 seconds</div>
              </>
            ) : (
              <>
                <div style={{ fontSize: '13px', color: '#eeeef5', marginBottom: '4px' }}>No {TABS.find(t => t.key === activeTab)?.label} found</div>
                <div style={{ fontSize: '11.5px', color: '#52525f', marginBottom: '14px' }}>Connect a cloud account or create your first resource</div>
                <button style={S.btn('primary')} onClick={() => setShowCreate(true)}>+ Add Resource</button>
              </>
            )}
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span style={{ fontSize: '12px', color: '#52525f' }}>
                {tabData.resources.length} resource{tabData.resources.length !== 1 ? 's' : ''}
                {tabData.metrics.issues > 0 && (
                  <span style={{ marginLeft: '8px', color: '#f43f5e' }}>· {tabData.metrics.issues} issue{tabData.metrics.issues !== 1 ? 's' : ''}</span>
                )}
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: '10px' }}>
              {tabData.resources.map(r => <ResourceCard key={r.id} r={r} />)}
            </div>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateModal
          activeTab={activeTab}
          onClose={() => setShowCreate(false)}
          onSuccess={() => {
            setShowCreate(false);
            setTimeout(() => fetchTab(activeTab, true), 1000);
          }}
        />
      )}
    </div>
  );
};

export default InfrastructureView;